/* FamilyTreeView — visual auto-built family tree (iter97.1).

   Layout:
       Parents row (ancestors)        ─── horizontal line between siblings ─── vertical drop ↓
       Spouse  ─── Trunk (deceased) ··· Siblings (dotted line)
                       │ vertical drop ↓
       Children row     (horizontal bus + vertical drops)
                       │
       Grandchildren row

   Each member card has: circular photo (or initial), name, year range,
   and a small leaf badge in the corner — green for living, gold for
   deceased. The deceased anchors the trunk as a larger oval card with a
   gold border.

   Includes a "View as List" toggle so visitors can flip to a flat
   accessible list. Auto-falls-back to the list when none of the
   members have recognised relationships. Responsive: stacks vertically
   on mobile while keeping the connector lines visible. */
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { List, Network, Leaf, ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react';

// ---------- Relationship → tree bucket ----------
const ANCESTORS = new Set([
  'father', 'mother', 'parent', 'stepfather', 'stepmother',
  'grandfather', 'grandmother', 'grandparent',
  'great-grandfather', 'great-grandmother', 'great-grandparent',
  'father-in-law', 'mother-in-law',
]);
const SPOUSES = new Set([
  'spouse', 'partner', 'husband', 'wife', 'fiancé', 'fiance', 'fiancée', 'fiancee',
]);
const SIBLINGS = new Set([
  'brother', 'sister', 'sibling', 'stepbrother', 'stepsister',
  'half-brother', 'half-sister',
  'brother-in-law', 'sister-in-law',
]);
const CHILDREN = new Set([
  'son', 'daughter', 'child', 'stepson', 'stepdaughter',
  'son-in-law', 'daughter-in-law',
]);

export const TREE_POSITIONS = [
  { value: 'auto', label: 'Auto (from relationship)' },
  { value: 'ancestor', label: 'Ancestor (above)' },
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'descendant', label: 'Child' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'other', label: 'Other / extended' },
];

export function deriveBucket(member) {
  const g = generationOf(member);
  if (typeof g === 'number') {
    if (g >= 2) return 'ancestor';
    if (g === 1) return 'ancestor';
    if (g === -1) return 'descendant';
    return 'grandchild';
  }
  return g;
}

/* Generation-aware relationship parsing (iter67 — unlimited generations).
   Returns:
     positive int  → ancestor generation  (+1 parents, +2 grandparents, +3 great-grandparents …)
     negative int  → descendant generation (-1 children, -2 grandchildren, -3 great-grandchildren …)
     'spouse' | 'sibling' | 'other' */
export function generationOf(member) {
  const override = (member.tree_position || 'auto').toLowerCase();
  if (override && override !== 'auto') {
    const map = { ancestor: 1, spouse: 'spouse', sibling: 'sibling', descendant: -1, grandchild: -2, other: 'other' };
    return map[override] ?? 'other';
  }
  const rel = (member.relationship || '').trim().toLowerCase().replace(/\s+/g, '-');
  if (!rel) return 'other';
  const greats = (rel.match(/great-?/g) || []).length;
  if (/(grandfather|grandmother|grandparent)$/.test(rel)) return 2 + greats;
  if (/(grandson|granddaughter|grandchild)$/.test(rel)) return -(2 + greats);
  if (ANCESTORS.has(rel)) return 1;
  if (SPOUSES.has(rel)) return 'spouse';
  if (SIBLINGS.has(rel)) return 'sibling';
  if (CHILDREN.has(rel)) return -1;
  return 'other';
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export const ancestorRowLabel = (n) =>
  n === 1 ? 'Parents & elders' : cap('great-'.repeat(n - 2) + 'grandparents');
export const descendantRowLabel = (n) =>
  n === 1 ? 'Children' : cap('great-'.repeat(n - 2) + 'grandchildren');

const yearOf = (s) => (typeof s === 'string' && s.length >= 4 ? s.slice(0, 4) : null);
const isLiving = (m) =>
  (typeof m.is_living === 'boolean') ? m.is_living : !m.death_date;

const initial = (name = '') => (name.trim().charAt(0) || '?').toUpperCase();

/* ---------- Theme palettes ---------- */
const PALETTES = {
  light: {
    text: '#1A3626',
    muted: '#5F6F66',
    rowLabel: '#7a7a73',
    cardBg: '#FFFFFF',
    cardBorder: 'rgba(26, 54, 38, 0.12)',
    line: 'rgba(26, 54, 38, 0.35)',
    trunkBorder: '#C5A059',          // gold
    listBtnText: '#1A3626',
    listBtnBorder: 'rgba(26,54,38,0.18)',
  },
  dark: {
    text: '#F4E5C2',
    muted: 'rgba(244,229,194,0.78)',
    rowLabel: '#D9B775',
    cardBg: '#FFFFFF',                // cards stay white on dark background — like the picture
    cardBorder: 'rgba(244,229,194,0.16)',
    line: 'rgba(244,229,194,0.55)',
    trunkBorder: '#D9B775',
    listBtnText: '#F4E5C2',
    listBtnBorder: 'rgba(244,229,194,0.45)',
  },
};

const LEAF_GREEN = '#5C8B49';   // living
const LEAF_GOLD = '#C5A059';    // deceased

/* ====================================================================== */
/*  Public component                                                       */
/* ====================================================================== */

/**
 * <FamilyTreeView
 *   deceasedName="Michael James Anderson"
 *   deceasedPhotoUrl="…"
 *   deceasedBirthYear="1948"
 *   deceasedDeathYear="2023"
 *   members={[...]}
 *   onMemberClick={(m) => ...}
 *   theme="light" | "dark"
 * />
 */
export function FamilyTreeView({
  deceasedName,
  deceasedPhotoUrl = null,
  deceasedBirthYear = null,
  deceasedDeathYear = null,
  members = [],
  onMemberClick,
  theme = 'light',
}) {
  const [viewMode, setViewMode] = useState('tree');  // 'tree' | 'list'

  const palette = PALETTES[theme] || PALETTES.light;

  const buckets = useMemo(() => {
    const anc = new Map();
    const desc = new Map();
    const b = { spouse: [], sibling: [], other: [] };
    for (const m of members) {
      const g = generationOf(m);
      if (typeof g === 'number' && g > 0) {
        if (!anc.has(g)) anc.set(g, []);
        anc.get(g).push(m);
      } else if (typeof g === 'number') {
        if (!desc.has(-g)) desc.set(-g, []);
        desc.get(-g).push(m);
      } else {
        b[g].push(m);
      }
    }
    // Deepest ancestors render first (top of the tree); children before grandchildren.
    b.ancestorRows = [...anc.entries()].sort((a, z) => z[0] - a[0]);
    b.descendantRows = [...desc.entries()].sort((a, z) => a[0] - z[0]);
    return b;
  }, [members]);

  const totalRecognised =
    buckets.spouse.length +
    buckets.sibling.length +
    buckets.ancestorRows.reduce((n, [, p]) => n + p.length, 0) +
    buckets.descendantRows.reduce((n, [, p]) => n + p.length, 0);

  // Auto-fall-back to a flat list when there are NO recognised relationships
  // — the tree needs structure to make sense.
  const forceList = totalRecognised === 0 && buckets.other.length > 0;
  const effectiveMode = forceList ? 'list' : viewMode;

  return (
    <div className="w-full" data-testid="family-tree-view">
      <TreeToolbar
        palette={palette}
        viewMode={effectiveMode}
        onChange={setViewMode}
        disabled={forceList}
      />

      {effectiveMode === 'list' ? (
        <FlatList
          deceasedName={deceasedName}
          deceasedBirthYear={deceasedBirthYear}
          deceasedDeathYear={deceasedDeathYear}
          members={members}
          onMemberClick={onMemberClick}
          palette={palette}
        />
      ) : (
        <ZoomPanCanvas palette={palette}>
          <Tree
            buckets={buckets}
            deceased={{
              name: deceasedName,
              photoUrl: deceasedPhotoUrl,
              birthYear: deceasedBirthYear,
              deathYear: deceasedDeathYear,
            }}
            palette={palette}
            onMemberClick={onMemberClick}
          />
        </ZoomPanCanvas>
      )}

      {effectiveMode === 'tree' && (
        <div
          className="mt-7 text-center text-xs italic"
          style={{ color: palette.muted }}
          data-testid="family-tree-hint"
        >
          {onMemberClick ? "Tap any person to read their story." : null}
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/*  ZoomPanCanvas — pinch-zoom / wheel-zoom / drag-pan viewport (iter67)   */
/* ====================================================================== */
function ZoomPanCanvas({ children, palette }) {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [t, setT] = useState({ x: 0, y: 8, s: 1 });
  const pointers = useRef(new Map());
  const gesture = useRef(null); // { startDist, startMid, startT } | { startX, startY, startT }
  const movedRef = useRef(false);

  const clampScale = (s) => Math.min(3, Math.max(0.2, s));

  const fit = useCallback(() => {
    const vp = viewportRef.current;
    const ct = contentRef.current;
    if (!vp || !ct) return;
    const cw = ct.offsetWidth;
    const ch = ct.offsetHeight;
    if (!cw || !ch) return;
    const s = clampScale(Math.min(vp.clientWidth / cw, vp.clientHeight / ch, 1) * 0.96);
    setT({ s, x: (vp.clientWidth - cw * s) / 2, y: Math.max((vp.clientHeight - ch * s) / 2, 8) });
  }, []);

  const reset = useCallback(() => {
    const vp = viewportRef.current;
    const ct = contentRef.current;
    if (!vp || !ct) { setT({ x: 0, y: 8, s: 1 }); return; }
    setT({ s: 1, x: (vp.clientWidth - ct.offsetWidth) / 2, y: 8 });
  }, []);

  // Auto-fit on mount + whenever the viewport or content resizes
  // (desktop ⇄ tablet ⇄ mobile, or new family members added).
  useEffect(() => {
    fit();
    const ro = new ResizeObserver(() => fit());
    if (viewportRef.current) ro.observe(viewportRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [fit]);

  const zoomAt = useCallback((cx, cy, factor) => {
    setT((prev) => {
      const s = clampScale(prev.s * factor);
      const k = s / prev.s;
      return { s, x: cx - (cx - prev.x) * k, y: cy - (cy - prev.y) * k };
    });
  }, []);

  // Wheel zoom — attached manually so we can preventDefault (React wheel is passive).
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.12 : 0.9);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const midAndDist = () => {
    const pts = [...pointers.current.values()];
    const dx = pts[0].x - pts[1].x;
    const dy = pts[0].y - pts[1].y;
    return {
      dist: Math.hypot(dx, dy),
      mid: { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 },
    };
  };

  const onPointerDown = (e) => {
    const rect = viewportRef.current.getBoundingClientRect();
    pointers.current.set(e.pointerId, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    viewportRef.current.setPointerCapture?.(e.pointerId);
    movedRef.current = false;
    if (pointers.current.size === 2) {
      const { dist, mid } = midAndDist();
      gesture.current = { pinch: true, startDist: dist, startMid: mid, startT: t };
    } else if (pointers.current.size === 1) {
      gesture.current = { pinch: false, startX: e.clientX, startY: e.clientY, startT: t };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    const rect = viewportRef.current.getBoundingClientRect();
    pointers.current.set(e.pointerId, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    if (gesture.current.pinch && pointers.current.size >= 2) {
      const { dist, mid } = midAndDist();
      const g = gesture.current;
      const s = clampScale(g.startT.s * (dist / g.startDist));
      const k = s / g.startT.s;
      movedRef.current = true;
      setT({
        s,
        x: mid.x - (g.startMid.x - g.startT.x) * k,
        y: mid.y - (g.startMid.y - g.startT.y) * k,
      });
    } else if (!gesture.current.pinch) {
      const dx = e.clientX - gesture.current.startX;
      const dy = e.clientY - gesture.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
      if (movedRef.current) {
        setT({ s: gesture.current.startT.s, x: gesture.current.startT.x + dx, y: gesture.current.startT.y + dy });
      }
    }
  };

  const onPointerEnd = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
  };

  const btnStyle = {
    color: palette.listBtnText,
    border: `1px solid ${palette.listBtnBorder}`,
    backgroundColor: 'transparent',
  };

  return (
    <div className="relative" data-testid="family-tree-zoom-wrap">
      {/* Controls */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5 flex-wrap justify-end">
        <button type="button" onClick={() => zoomAt((viewportRef.current?.clientWidth || 0) / 2, (viewportRef.current?.clientHeight || 0) / 2, 1.25)}
                className="p-1.5 rounded-full" style={btnStyle} aria-label="Zoom in" data-testid="tree-zoom-in">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => zoomAt((viewportRef.current?.clientWidth || 0) / 2, (viewportRef.current?.clientHeight || 0) / 2, 0.8)}
                className="p-1.5 rounded-full" style={btnStyle} aria-label="Zoom out" data-testid="tree-zoom-out">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={fit}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold"
                style={btnStyle} data-testid="tree-fit-btn">
          <Maximize2 className="w-3 h-3" /> Fit to Screen
        </button>
        <button type="button" onClick={reset}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold"
                style={btnStyle} data-testid="tree-reset-btn">
          <RotateCcw className="w-3 h-3" /> Reset View
        </button>
      </div>

      <div
        ref={viewportRef}
        className="w-full overflow-hidden rounded-2xl select-none h-[420px] sm:h-[520px]"
        style={{ touchAction: 'none', cursor: 'grab', border: `1px dashed ${palette.listBtnBorder}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={(e) => {
          // Swallow the click that follows a drag/pinch so cards don't open.
          if (movedRef.current) { e.stopPropagation(); e.preventDefault(); movedRef.current = false; }
        }}
        data-testid="family-tree-zoom-canvas"
      >
        <div
          ref={contentRef}
          style={{
            transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})`,
            transformOrigin: '0 0',
            width: 'max-content',
          }}
        >
          {children}
        </div>
      </div>
      <div className="mt-2 text-center text-[10px] tracking-[0.18em] uppercase" style={{ color: palette.rowLabel }}>
        Drag to pan · Pinch or scroll to zoom
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Toolbar (Legend + View as List)                                       */
/* ====================================================================== */
function TreeToolbar({ palette, viewMode, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-6" data-testid="family-tree-toolbar">
      <div
        className="flex items-center flex-wrap gap-4 text-[11px] tracking-[0.22em] uppercase font-medium"
        style={{ color: palette.rowLabel }}
      >
        <span className="font-semibold">Legend:</span>
        <span className="inline-flex items-center gap-1.5">
          <Leaf className="w-3.5 h-3.5" style={{ color: LEAF_GREEN }} fill={LEAF_GREEN} />
          Living
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Leaf className="w-3.5 h-3.5" style={{ color: LEAF_GOLD }} fill={LEAF_GOLD} />
          Deceased
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-5 h-px"
            style={{ backgroundColor: palette.line }}
          />
          Relationship
        </span>
      </div>
      {!disabled && (
        <div className="flex items-center gap-1.5 rounded-full p-1"
             style={{ border: `1px solid ${palette.listBtnBorder}` }}>
          <ToolBtn active={viewMode === 'tree'}
                   onClick={() => onChange('tree')}
                   palette={palette}
                   Icon={Network}
                   label="Tree"
                   testid="family-tree-view-mode-tree" />
          <ToolBtn active={viewMode === 'list'}
                   onClick={() => onChange('list')}
                   palette={palette}
                   Icon={List}
                   label="View as List"
                   testid="family-tree-view-mode-list" />
        </div>
      )}
    </div>
  );
}

function ToolBtn({ active, onClick, palette, Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase font-medium transition-colors"
      style={{
        backgroundColor: active ? '#1A3626' : 'transparent',
        color: active ? '#F4E5C2' : palette.listBtnText,
      }}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

/* ====================================================================== */
/*  Tree layout                                                            */
/* ====================================================================== */
/* iter97.10 — Stability rewrite.
   PRINCIPLE: the memorial person (trunk) is the visual anchor and MUST
   stay in the geometric center of the tree no matter how many relatives
   are added. The previous flex-wrap layout reflowed the trunk into a
   neighbouring row once spouses + siblings overflowed.

   New rules:
     • Each row is its own horizontally-scrollable strip.
     • The trunk row is a 3-column flex layout
       [SPOUSE CELL] [TRUNK (centered, shrink-0)] [SIBLINGS CELL]
       Each side cell scrolls horizontally inside itself if needed —
       the trunk never moves.
     • Parents, children, grandchildren rows scroll horizontally on
       overflow (no wrap, so the bus line stays connected). */
/*  iter97.13 — Trunk layout rewrite. The previous "spouse beside trunk
    in a 3-column grid" approach worked for ≤1 spouse but broke at
    production scale: Marilyn Monroe has 3 spouses, the leftmost two
    were clipped off-screen because they sat inside a narrow side cell
    with `justify-end` and the horizontal scroll affordance wasn't
    obvious. Solution: trunk lives in its own centered row, and spouses
    + siblings use the same battle-tested `ScrollableRow` pattern as
    ancestors / descendants — they can never clip because they have
    proper edge padding, label headers, and the whole row scrolls as a
    unit when content overflows. */
function Tree({ buckets, deceased, palette, onMemberClick }) {
  return (
    <div
      className="px-2"
      style={{ width: 'max-content', minWidth: '100%' }}
      data-testid="family-tree-canvas"
    >

      {/* ANCESTOR generations — deepest (great-…grandparents) at the top */}
      {buckets.ancestorRows.map(([gen, people]) => (
        <React.Fragment key={`anc-${gen}`}>
          <ScrollableRow
            people={people}
            palette={palette}
            onClick={onMemberClick}
            rowId={gen === 1 ? 'ancestors' : `ancestors-${gen}`}
            connectorStyle="solid"
            label={ancestorRowLabel(gen)}
          />
          <VLine palette={palette} />
        </React.Fragment>
      ))}

      {/* SPOUSE row — labeled and horizontally scrollable. Sits directly
          above the trunk so the visual relationship is still clear, but
          never clips because it owns the full canvas width. */}
      {buckets.spouse.length > 0 && (
        <>
          <ScrollableRow
            people={buckets.spouse}
            palette={palette}
            onClick={onMemberClick}
            rowId="spouses"
            connectorStyle="solid"
            label={buckets.spouse.length > 1 ? 'Spouses' : 'Spouse'}
          />
          <VLine palette={palette} />
        </>
      )}

      {/* TRUNK — geometric center, dedicated row, can never be pushed. */}
      <div
        className="flex justify-center"
        data-testid="tree-trunk-row"
      >
        <TrunkCard deceased={deceased} palette={palette} />
      </div>

      {/* SIBLINGS row — directly below trunk with dotted connector. */}
      {buckets.sibling.length > 0 && (
        <>
          <VLine palette={palette} dashed />
          <ScrollableRow
            people={buckets.sibling}
            palette={palette}
            onClick={onMemberClick}
            rowId="siblings"
            connectorStyle="dotted"
            label={buckets.sibling.length > 1 ? 'Siblings' : 'Sibling'}
          />
        </>
      )}

      {/* DESCENDANT generations — children, grandchildren, great-… */}
      {buckets.descendantRows.map(([gen, people]) => (
        <React.Fragment key={`desc-${gen}`}>
          <VLine palette={palette} />
          <ScrollableRow
            people={people}
            palette={palette}
            onClick={onMemberClick}
            rowId={gen === 1 ? 'children' : gen === 2 ? 'grandchildren' : `descendants-${gen}`}
            connectorStyle="solid"
            label={descendantRowLabel(gen)}
          />
        </React.Fragment>
      ))}

      {/* EXTENDED / OTHER row — same scrollable strip pattern */}
      {buckets.other.length > 0 && (
        <>
          <VLine palette={palette} dashed />
          <ScrollableRow
            people={buckets.other}
            palette={palette}
            onClick={onMemberClick}
            rowId="extended"
            connectorStyle="solid"
            label="Extended family"
          />
        </>
      )}
    </div>
  );
}

/* A row of sibling-level cards. Horizontally scrollable on overflow, so
   adding many cousins / grandchildren / etc never causes the parent rows
   to reflow and never pushes the trunk out of center. */
function ScrollableRow({ people, palette, onClick, rowId, connectorStyle = 'solid', label }) {
  if (!people.length) return null;
  const isMulti = people.length > 1;
  return (
    <div data-testid={`tree-row-${rowId}`}>
      {label && (
        <div
          className="text-[10px] tracking-[0.32em] uppercase font-semibold text-center mb-3"
          style={{ color: palette.rowLabel }}
        >
          {label}
        </div>
      )}
      {/* iter67 — inner scrolling removed: the ZoomPanCanvas owns pan/zoom,
          so rows render at natural width and the canvas fits/centers them. */}
      <div className="flex flex-col w-full">
          {isMulti && (
            <div className="flex justify-center px-6 sm:px-10" aria-hidden="true">
              <div
                className="h-px w-full max-w-[640px]"
                style={{
                  borderTop: `1px ${connectorStyle === 'dotted' ? 'dashed' : 'solid'} ${palette.line}`,
                }}
              />
            </div>
          )}
          <div className="flex items-stretch justify-center gap-3 sm:gap-4 pt-3 px-4 sm:px-6">
            {people.map((m) => (
              <div key={m.family_id} className="flex flex-col items-center shrink-0">
                {isMulti && <VLineSmall palette={palette} />}
                <MemberCard member={m} palette={palette} onClick={onClick} />
              </div>
            ))}
          </div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Connectors                                                             */
/* ====================================================================== */
function VLine({ palette, dashed = false }) {
  return (
    <div className="flex justify-center" aria-hidden="true">
      <span
        className="block h-6 sm:h-7 w-px"
        style={{
          borderLeft: `1px ${dashed ? 'dashed' : 'solid'} ${palette.line}`,
        }}
      />
    </div>
  );
}

function VLineSmall({ palette }) {
  return (
    <span
      className="block h-3 w-px"
      style={{ borderLeft: `1px solid ${palette.line}` }}
      aria-hidden="true"
    />
  );
}

function HLine({ palette, style = 'solid' }) {
  return (
    <span
      className="hidden sm:block h-px w-6"
      style={{
        borderTop: `1px ${style === 'dotted' ? 'dashed' : 'solid'} ${palette.line}`,
      }}
      aria-hidden="true"
    />
  );
}

/* ====================================================================== */
/*  Member card                                                            */
/* ====================================================================== */
function MemberCard({ member, palette, onClick }) {
  const living = isLiving(member);
  const leafColor = living ? LEAF_GREEN : LEAF_GOLD;
  const by = yearOf(member.birth_date);
  const dy = yearOf(member.death_date);
  const yearRange = by
    ? (dy ? `${by} – ${dy}` : `${by} – Living`)
    : (dy ? `– ${dy}` : null);
  // iter97.12 — Display the relationship label under the name on every
  // leaf so visitors know who this person is (Father, Mother, Brother,
  // Daughter, Grandson, etc.). Capitalised, gracefully falls back to
  // "Family" if no relationship string was provided.
  const relationship = (member.relationship || '').trim();
  const relationshipLabel = relationship
    ? relationship.charAt(0).toUpperCase() + relationship.slice(1).toLowerCase()
    : 'Family';

  const Wrapper = 'div'; // div (not button) so the nested memorial link stays valid HTML

  return (
    <Wrapper
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? () => onClick(member) : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(member); } : undefined}
      className={`relative rounded-2xl flex flex-col items-center px-4 py-3 text-center transition-shadow ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
      style={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        minWidth: 132,
        maxWidth: 180,
        boxShadow: '0 4px 18px -12px rgba(0,0,0,0.18)',
      }}
      data-testid={`tree-leaf-${member.family_id}`}
    >
      <Avatar
        photoUrl={member.photo_url}
        name={member.name}
        size={56}
      />
      <div
        className="mt-2 font-serif text-sm leading-tight line-clamp-2"
        style={{ color: PALETTES.light.text }}  /* always dark on white card */
      >
        {member.name}
      </div>
      <div
        className="mt-0.5 text-[10px] tracking-[0.18em] uppercase font-semibold"
        style={{ color: LEAF_GOLD }}
        data-testid={`tree-leaf-relationship-${member.family_id}`}
      >
        {relationshipLabel}
      </div>
      {yearRange && (
        <div
          className="mt-0.5 text-[10px] tracking-[0.12em] font-medium"
          style={{ color: PALETTES.light.muted }}
        >
          {yearRange}
        </div>
      )}
      {/* iter97.10 — "View memorial" badge surfaces ONLY when the linked
          memorial is paid + published. Backend strips linked_memorial_slug
          from the public payload for unpaid / draft / private ones, so
          this badge is automatically privacy-gated. */}
      {member.linked_memorial_slug && (
        <a
          href={`/m/${member.linked_memorial_slug}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold hover:underline"
          style={{
            color: LEAF_GOLD,
            backgroundColor: 'rgba(197, 160, 89, 0.10)',
            border: `1px solid ${LEAF_GOLD}40`,
          }}
          data-testid={`tree-leaf-linked-${member.family_id}`}
        >
          View memorial →
        </a>
      )}
      <Leaf
        className="absolute bottom-2 right-2 w-3.5 h-3.5"
        style={{ color: leafColor }}
        fill={leafColor}
        aria-label={living ? 'Living' : 'Deceased'}
      />
    </Wrapper>
  );
}

/* ====================================================================== */
/*  Trunk card (the deceased — bigger, oval, gold border)                  */
/* ====================================================================== */
function TrunkCard({ deceased, palette }) {
  const yearRange = deceased.birthYear
    ? (deceased.deathYear ? `${deceased.birthYear} – ${deceased.deathYear}` : `${deceased.birthYear}`)
    : null;
  return (
    <div
      className="relative flex flex-col items-center px-5 py-4 sm:py-5 text-center shrink-0"
      style={{
        backgroundColor: '#FFFFFF',
        border: `2px solid ${palette.trunkBorder}`,
        borderRadius: '32px',
        minWidth: 168,
        maxWidth: 240,
        boxShadow: '0 14px 30px -18px rgba(197, 160, 89, 0.55)',
      }}
      data-testid="tree-trunk"
    >
      <Avatar
        photoUrl={deceased.photoUrl}
        name={deceased.name}
        size={86}
        ring
      />
      <div
        className="mt-3 font-serif text-base sm:text-lg leading-tight"
        style={{ color: PALETTES.light.text }}
      >
        {deceased.name}
      </div>
      {yearRange && (
        <div
          className="mt-0.5 text-[11px] tracking-[0.14em] font-medium"
          style={{ color: PALETTES.light.muted }}
        >
          {yearRange}
        </div>
      )}
      <Leaf
        className="absolute bottom-2 right-3 w-4 h-4"
        style={{ color: LEAF_GOLD }}
        fill={LEAF_GOLD}
        aria-label="Deceased"
      />
    </div>
  );
}

/* ====================================================================== */
/*  Avatar (photo or initial)                                              */
/* ====================================================================== */
function Avatar({ photoUrl, name, size = 48, ring = false }) {
  // iter97.12 — Track image-load errors and fall back to initials so a
  // broken photo URL never leaves the trunk / leaf showing an empty
  // square. Critical for the deceased trunk on production where
  // `profile_photo_url` may be malformed across env redirects.
  const [errored, setErrored] = useState(false);
  const ringStyle = ring
    ? { boxShadow: `0 0 0 3px #FFFFFF, 0 0 0 4px ${LEAF_GOLD}` }
    : {};
  if (photoUrl && !errored) {
    return (
      <img
        src={photoUrl}
        alt={name || ''}
        loading="lazy"
        onError={() => setErrored(true)}
        className="rounded-full object-cover"
        style={{ width: size, height: size, ...ringStyle }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-serif"
      style={{
        width: size,
        height: size,
        backgroundColor: 'rgba(26, 54, 38, 0.06)',
        color: '#1A3626',
        fontSize: size * 0.42,
        border: '1px solid rgba(26, 54, 38, 0.12)',
        ...ringStyle,
      }}
    >
      {initial(name)}
    </div>
  );
}

/* ====================================================================== */
/*  Flat list mode (also used as the fallback)                             */
/* ====================================================================== */
function FlatList({ deceasedName, deceasedBirthYear, deceasedDeathYear, members, onMemberClick, palette }) {
  const trunkYears = deceasedBirthYear
    ? (deceasedDeathYear ? `${deceasedBirthYear} – ${deceasedDeathYear}` : deceasedBirthYear)
    : '';
  return (
    <div data-testid="family-tree-fallback-list">
      <div
        className="text-center text-[10px] tracking-[0.32em] uppercase font-semibold mb-4"
        style={{ color: palette.rowLabel }}
      >
        Family of {deceasedName}{trunkYears ? ` · ${trunkYears}` : ''}
      </div>
      <ul
        className="max-w-md mx-auto divide-y rounded-xl overflow-hidden"
        style={{
          borderTop: `1px solid ${palette.cardBorder}`,
          borderBottom: `1px solid ${palette.cardBorder}`,
          borderColor: palette.cardBorder,
        }}
      >
        {members.map((m) => {
          const living = isLiving(m);
          const Wrapper = onMemberClick ? 'button' : 'div';
          return (
            <li key={m.family_id} style={{ borderBottom: `1px solid ${palette.cardBorder}` }}>
              <Wrapper
                type={onMemberClick ? 'button' : undefined}
                onClick={onMemberClick ? () => onMemberClick(m) : undefined}
                className="w-full px-4 py-3 flex items-center gap-3 text-left"
                style={{ backgroundColor: palette.cardBg }}
                data-testid={`family-fallback-row-${m.family_id}`}
              >
                <Avatar photoUrl={m.photo_url} name={m.name} size={36} />
                <span className="flex-1 min-w-0">
                  <span
                    className="block font-serif text-[15px] truncate"
                    style={{ color: PALETTES.light.text }}
                  >
                    {m.name}
                  </span>
                  <span
                    className="block text-[10px] tracking-[0.22em] uppercase mt-0.5"
                    style={{ color: PALETTES.light.muted }}
                  >
                    {m.relationship || 'Family'}
                  </span>
                </span>
                <Leaf
                  className="w-3.5 h-3.5 shrink-0"
                  style={{ color: living ? LEAF_GREEN : LEAF_GOLD }}
                  fill={living ? LEAF_GREEN : LEAF_GOLD}
                />
              </Wrapper>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default FamilyTreeView;
